/* eslint-disable no-case-declarations */
// eslint-disable-next-line no-use-before-define
import _debounce from 'lodash/debounce';
import _isNil from 'lodash/isNil';
import dynamic from 'next/dynamic';
import { toast } from 'react-toastify';
import React from 'react';
import TreedisPublicDAO from '../api/TreedisPublicDAO';

import { Tour, TourType } from '@/_models/Tour';
import BaseTour from './tour/BaseTour';
import BottomBar from './tour/game/BottomBar';

import { b64DecodeUnicode } from '@/_helpers/all';
import GameplayScreen from './tour/game/GameplayScreen';

import LoadingProgress from '@/components/common/LoadingProgress';
import { ELockInfo, TLockItem } from '@/components/tour/game/NewLocks/LockInfoPopup/types';
import {
  EMPTY_LOCATION_ITEM,
  GameCondition,
  GameLock,
  GameLockPathTransitionType,
  LocationSrc,
  LOCK_SOLUTION_SEPARATOR,
  LockLocationType,
  LockType,
} from '@/_models/Game';
import { ChapterType } from '@/_models/game/GameBook';
import { Tag } from '@/_models/Tag';
import ClipScreen from './tour/game/ClipScreen';
import FinishScreen from './tour/game/FinishScreen';
import {
  getLockByPolygonId,
  getLockByTagId,
  getTagByLockLocationItem,
} from './tour/game/services/LockHandle';
import SimpleFinishScreen from './tour/game/SimpleFinishScreen';
import { GameStatus } from './types';
import { addToBackpack } from '@/_helpers/backpack';
import socket from './util/socket';
// import { isImage } from '@/_helpers/base';
import { TourActionType, TourVSHtml } from '@/_models/virtualStaging/VSObject';
import { getVersion } from './util';
import { AudioModule, MusicObj, MusicObjectKey } from '../components-new/common/AudioModule';
import {
  collectPopupComponentType,
  makeCollectPopupComponent,
} from '@/_models/virtualStaging/HTMLMesh/component/CollectPopup/CollectPopup.component';
import { createCollectHTML } from '@/_models/virtualStaging/HTMLMesh/utils/createCollectHTML';
import { UserSubscription } from '@/_models/User';
import DeviceManager from '@/components/util/deviceManager';
import { asyncStore } from '@/_helpers/base';
import {
  makePathFinderLineComponent,
  pathFinderLineComponentType,
} from '@/_models/virtualStaging/PathFinderLine/component/PathFinderLine.component';
import { extractSweepIdFromURL } from '@/utils/extractSweepIdFromURL';
import {
  makePathDestinationMeshComponent,
  pathDestinationMeshComponentType,
} from '@/_models/virtualStaging/HTMLMesh/component/PathDestinationMesh';
import { createPathDesinationHTML } from '@/_models/virtualStaging/HTMLMesh/utils/createPathDesinationHTML';
import { ScreenNavigationArrow } from '@/components/tour/game/ScreenNavigationArrow';
import { mondelezTourVideos } from './tour/config';
import MondelezTabletVideo from './tour/regular/mondelez-tablet-video/MondelezTabletVideo';

const ConditionModal = dynamic(() => import('./tour/game/ConditionModal'), {
  ssr: false,
});

const GameLockPopup = dynamic(() => import('./tour/regular/popups/game/GameLock'), {
  ssr: false,
});

const ConfettiVisualEffect = dynamic(() => import('./tour/game/ConfettiVisualEffect'), {
  ssr: false,
});

const LockInfoPopup = dynamic(() => import('./tour/game/NewLocks/LockInfoPopup'), {
  ssr: false,
});

const NewHintModal = dynamic(() => import('./tour/game/NewHintModal'), {
  ssr: false,
});

export enum HintLabels {
  HintA,
  HintB,
  Solution,
}

const audioHandler = AudioModule.getInstance();

// lockId:{attempts, disable}
export const gameLockData: Record<number, { attempts: number; disable: boolean }> = {};

export default class GameTour extends BaseTour {
  resetSequenceUserSolutionAfter = null;

  lockUserSolution = new Map<number, string>();

  pathComponent: any;

  sweepGraph: any;

  pathArrowRef: any;

  pathFinderDestinationMesh: any;

  pathFinderDestinationMeshNode: any;

  constructor(props: any) {
    super(props, TourType.Game);
    this.state = {
      ...this.state,
      showcaseLoaded: true,
      // temp
      username: '',
      backpackNewItems: 0,
      gameStatus: GameStatus.NotStarted,
      activeHintObject: null,
      showHintModal: false,
      showBook: false,
      finalTime: 0,
      finalPoints: 0,
      lockSolved: null,
      game: null,
      tour: null,
      sequenceUserSolution: '',
      tagsBackpack: [],
      polygonsBackpack: [],
      conditionsBackpack: [],
      collectItemsBackpack: [],
      // gameplay prop to react
      canInitPolygons: false,
      gameLoaded: false,
      tourLoaded: false,
      conditionPopUp: null,
      showIntro: false,
      guide: false,
      showConfetti: false,
      showNewHintModal: null, // null or activeLockId
      locksState: {},
      settingsOpen: false,
      currentCondition: null,
      isClosedGameGenially: false,
      isShowPathLockByExperience: true,
      endgameData: null,
      // Hub
      showParticipantsReel: false,
      participantId: '',
      isEnableNextStep: true,
      mondelezTabletVideoId: undefined,
      pathStepsBefore: {},
      distanceToNextPathStep: undefined,
    };
    this.pathArrowRef = React.createRef();
  }

  decodeEndGameData(endgameData: string): {
    username: string;
    finalPoints: number;
    finalTime: number;
  } {
    const paramsArray = b64DecodeUnicode(endgameData).split(',');
    const time = parseInt(paramsArray.pop(), 10);
    const points = parseInt(paramsArray.pop(), 10);
    const name = paramsArray.join('');
    return {
      username: name,
      finalPoints: points,
      finalTime: time,
    };
  }

  setEndGameData(endgameData: string) {
    const gameData = this.decodeEndGameData(endgameData);
    this.setState({
      endgameData: {
        gameStatus: GameStatus.SuccessfullyEnded,
        gameLoaded: true,
        showIntro: false,
        startScreenOn: false,
        showcaseLoaded: true,
        ...gameData,
      },
      gameStatus: GameStatus.SuccessfullyEnded,
      ...gameData,
    });
  }

  componentDidMount() {
    // For tours (not asset pages) we want to set overflow hidden
    // It avoids a bug where the scroll bar in Safari appears for a split second (TB-3322)
    if (document.documentElement && !document.location.pathname.includes('/asset/')) {
      document.documentElement.style.overflow = 'hidden';
    }

    const { /* gameId, */ matterId, endgameData, tourData } = this.props;
    if (!tourData?.tour) {
      toast.error('Tour not found');
      return;
    }

    // @ts-ignore
    window.tourId = tourData.tour.id;
    // @ts-ignore
    window.multiPath = [];

    // if endgameData decode it and set endgame status
    this.listenSockets();
    if (endgameData) {
      this.setEndGameData(endgameData);
    }
    Promise.all([/* this.getGame(gameId), */ this.getTour({ slug: matterId, tourData })]);
  }

  async componentDidUpdate(prevProps: Readonly<any>, prevState: Readonly<any>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);

    // Path finder logic
    if (this.state.pathfinderDataState.currentPathStepId && !this.pathComponent) {
      await this.createPathFinderLine();
      // TODO temporarily hidden until mesh alignment issues are fixed (TB-2726)
      // await this.createPathFinderDestinationMesh();
      this.showCaseTemp.mpSDK.Sweep.current.subscribe((currentSweep) => {
        if (currentSweep.sid !== '') {
          this.updatePathFinderLine();
          // if (this.pathFinderDestinationMesh)
          // this.updatePathFinderDestination(currentSweep);
          if (this.pathComponent && !this.pathComponent.sphereArr.length)
            this.pathArrowRef.current.style.opacity = 0;
        }
      });
    } else if (
      prevState.pathfinderDataState.currentPathStepId !==
        this.state.pathfinderDataState.currentPathStepId &&
      this.pathComponent
    ) {
      await this.updatePathFinderLine();
      // if (this.pathFinderDestinationMeshNode) await this.pathFinderDestinationMeshNode.stop();
      // await this.createPathFinderDestinationMesh();
    }

    // Handle the isShowPathLockByExperience state update
    if (prevState.experiencePopUpIsOpen !== this.state.experiencePopUpIsOpen) {
      this.setState({
        isShowPathLockByExperience: !this.state.experiencePopUpIsOpen,
      });
    }

    // Handle subtitle toggling based on popup state
    const { vsActionPopOn, openedVSAction, popupsOpen, activeSweepGroup, hideSubtitlesGroupId } =
      this.state;

    const vsActionIframeInterupt =
      openedVSAction?.type === TourActionType.Iframe && !openedVSAction?.dontInterruptBgAudio;
    const vsActionMedia = [TourActionType.Music, TourActionType.Video].includes(
      openedVSAction?.type || '',
    );

    if (
      (prevState.vsActionPopOn !== vsActionPopOn || prevState.popupsOpen !== popupsOpen) &&
      vsActionPopOn &&
      (vsActionMedia || vsActionIframeInterupt)
    ) {
      this.toggleSubtitle(popupsOpen).then(() => {
        const newHideSubtitlesGroupId = popupsOpen ? activeSweepGroup : null;
        if (hideSubtitlesGroupId !== newHideSubtitlesGroupId) {
          this.setState({ hideSubtitlesGroupId: newHideSubtitlesGroupId });
        }
      });
    }
  }

  // Temporary logic for a specific mondelez tour
  handleMondelezTabletVideos = (lockName: string) => {
    // if tour is not loaded yet, postpone the logic
    if (!this.state.showcaseLoaded || this.state.startScreenOn) {
      setTimeout(() => this.handleMondelezTabletVideos(lockName), 3000);
      return;
    }

    if (this.state?.tour?.matterId === 'mzs1xwR3pPy') {
      // If we already saw the video, don't show it again
      const item = mondelezTourVideos.find((i) => lockName.trim() === i.lockName);
      if (item) {
        this.setState({
          mondelezTabletVideoId: item.videoId,
        });
      }
    }
  };

  getNextPathSweep = (targetPath) => {
    let targetUuid;
    if (targetPath.locationSrc === LocationSrc.chooseSweep && targetPath.sweepUuid)
      targetUuid = targetPath.sweepUuid;
    if (targetPath.locationSrc === LocationSrc.tourLink && targetPath.locationSmartLink)
      targetUuid = extractSweepIdFromURL(targetPath.locationSmartLink);
    return this.state.sweeps.find((el) => el.uuid === targetUuid);
  };

  async createPathFinderDestinationMesh() {
    const targetPath = this.state.game.paths.paths.find(
      (path) => path.id === this.state.pathfinderDataState.currentPathStepId,
    );
    const sweep = this.getNextPathSweep(targetPath);

    if (targetPath.transitionType !== GameLockPathTransitionType.MANUAL) {
      this.pathFinderDestinationMesh = null;
      // this.pathFinderDestinationMeshNode = null;
      return;
    }

    let media = null;
    if (this.state.game.paths?.pathSetting.icon)
      media = `${process.env.NEXT_PUBLIC_CDN}/games/${this.props.gameId}/locks/${this.state.game.paths.id}/${this.state.game.paths.pathSetting.icon}`;
    const tagData = {
      title: targetPath.title,
    };
    const dimensions = {
      width: 500 * 0.75,
      height: 200 * 0.6,
    };
    const { html, imageContainer } = createPathDesinationHTML(tagData, media, dimensions);
    if (imageContainer) {
      imageContainer.src = media;
      await imageContainer.decode();
    }
    await this.showCaseTemp.showcase.Scene.register(
      pathDestinationMeshComponentType,
      makePathDestinationMeshComponent,
    );
    const modelNode: any = await this.showCaseTemp.mpSDK.Scene.createNode();
    this.pathFinderDestinationMesh = await modelNode.addComponent(
      pathDestinationMeshComponentType,
      {
        html,
        media,
        popupScale: 16,
        dimensions,
        sweep,
      },
    );
    this.pathFinderDestinationMesh.setHostPose(this.camera.parent);
    this.pathFinderDestinationMeshNode = modelNode;
    await modelNode.start();
  }

  updatePathFinderDestination(currentSweep) {
    const targetPath = this.state.game.paths.paths.find(
      (path) => path.id === this.state.pathfinderDataState.currentPathStepId,
    );
    const sweep = this.getNextPathSweep(targetPath);
    if (currentSweep.sid === sweep.sid) this.pathFinderDestinationMesh.setVisible(false);
    else this.pathFinderDestinationMesh.setVisible(true);
  }

  async createPathFinderLine() {
    this.sweepGraph = await this.showCaseTemp.mpSDK.Sweep.createGraph();
    for (const { src, dst, weight } of this.sweepGraph.edges) {
      this.sweepGraph.setEdge({ src, dst, weight: weight ** 1.5 });
    }

    await this.showCaseTemp.showcase.Scene.register(
      pathFinderLineComponentType,
      makePathFinderLineComponent,
    );
    const modelNode: any = await this.showCaseTemp.mpSDK.Scene.createNode();
    this.pathComponent = await modelNode.addComponent(pathFinderLineComponentType, {
      camera: this.camera,
      arrowRef: this.pathArrowRef,
      pathOffsetY: -1.15,
    });
    await modelNode.start();
    this.pathComponent.inputs.onDistanceChanged = (distance: number) => {
      this.setState({
        distanceToNextPathStep: distance,
      });
    };
    await this.updatePathFinderLine();
  }

  setPassedPath = (stepId: number, isReset = false) => {
    const obj = this.state.pathStepsBefore;
    if (obj[stepId] === undefined || obj[stepId] === null) {
      obj[stepId] = 0;
    } else if (isReset) {
      obj[stepId] = undefined;
    } else {
      obj[stepId] = 1;
    }
    this.setState({
      pathStepsBefore: obj,
    });
  };

  async updatePathFinderLine() {
    const paths = this.state?.game?.paths || [];
    const targetPath = paths
      .flatMap((gamePath) => gamePath.paths || [])
      .find((singlePath) => singlePath.id === this.state?.pathfinderDataState?.currentPathStepId);
    if (!targetPath?.disableTeleport) this.setState({ isEnableNextStep: true });
    if (
      targetPath?.disableNextBeforeCompletingTask &&
      !_isNil(this.state.pathStepsBefore[this.state?.pathfinderDataState?.currentPathStepId])
    ) {
      this.setState({ isEnableNextStep: false });
    }
    if (targetPath?.transitionType !== GameLockPathTransitionType.MANUAL) {
      this.pathComponent.inputs.path = [];
      return;
    }

    const sweep = this.getNextPathSweep(targetPath);
    if (!sweep) return;

    const startSweep = this.sweepGraph.vertex(this.state.currentSweep.sid) || {};
    const endSweep = this.sweepGraph.vertex(sweep.sid) || {};

    const AStarRunner = this.showCaseTemp.mpSDK.Graph.createAStarRunner(
      this.sweepGraph,
      startSweep,
      endSweep,
    );
    const { path } = AStarRunner.exec();
    this.pathComponent.inputs.path = path;

    if (targetPath?.disableTeleport) {
      const isEnableNextStep =
        path.length === 1 ||
        (path.length >= 2 &&
          (path.at(-1).data.sid === this.state.currentSweep.sid ||
            path.at(-2).data.sid === this.state.currentSweep.sid));
      this.setState({ isEnableNextStep });
    }

    AStarRunner.dispose();
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.takePosition);
    document.removeEventListener('contextmenu', this.disableContext);

    if (socket) {
      socket.off('video_user_joined');
    }
  }

  setMusicEffectForLock = async (lock: GameLock, soundEffectKey = 'finishMusic') => {
    const {
      game: { id: gameId },
    } = this.state;
    const {
      recoilState: { soundOn, volume },
    } = this.props;
    if (!soundOn) {
      return;
    }
    if (lock.type === LockType.Sequence || lock.type === LockType.CollectItems) {
      const sound = lock[soundEffectKey];
      const { importAudioData, changeSelectedAudioLoop, changeSelectedAudioVolume } = audioHandler;
      if (sound) {
        const data = {
          gameId,
          id: lock.id,
          sound,
        };
        await importAudioData(MusicObjectKey.GAME_SELECTED_AUDIO, [data]);
        changeSelectedAudioLoop(MusicObjectKey.GAME_SELECTED_AUDIO, data.id, false);
        changeSelectedAudioVolume(MusicObjectKey.GAME_SELECTED_AUDIO, data.id, volume);
      }
    }
  };

  showSequenceLockUserResult = async (lock: GameLock, temp: string) => {
    this.setState({
      gameLockPopOn: true,
      openedLock: lock,
      userAnswerForGameLock: temp,
      sequenceUserSolution: '',
    });
  };

  handleTagLock = (tag: Tag): boolean => {
    const {
      game: { locks },
    } = this.state;
    if (tag?.gameBackpack && !this.state.tagsBackpack.some(({ id }) => tag.id === id)) {
      this.setState({
        tagsBackpack: [tag, ...this.state.tagsBackpack],
      });
      // return false; //THIS PREVENTING TO OPEN LOCK Assigned to tag
    }
    const lock = getLockByTagId(locks, tag?.defaultSid || tag?.sid);
    if (!lock) {
      return false;
    }
    if (gameLockData[lock?.id]?.disable) {
      return true;
    }
    if (lock.type === LockType.CollectItems) {
      this.handleCollectLock(lock, tag?.defaultSid || tag?.sid);
    } else if (lock.type === LockType.Sequence) {
      this.handleSequenceLock(lock, tag?.defaultSid || tag?.sid);
    } else if (lock && !gameLockData[lock?.id]?.disable) {
      this.setState({
        gameLockPopOn: true,
        openedLock: lock,
        closedElementPopup: false,
      });
    }
    return true;
  };

  handlePolygonLock = (tBoxId: number): boolean | undefined => {
    const {
      game: { locks },
    } = this.state;
    const lock = getLockByPolygonId(locks, tBoxId);

    if (!lock) {
      // Handle by default
      return true;
    }
    if (gameLockData[lock?.id]?.disable) {
      // nothing happened
      return false;
    }

    this.setState({
      vsActionPopOn: false,
    });

    if (lock.type === LockType.CollectItems) {
      this.handleCollectLock(lock, String(tBoxId));
    } else if (lock.type === LockType.Sequence) {
      this.handleSequenceLock(lock, String(tBoxId), () =>
        this.modelNodes.forEach((node) => node[1] && node[1].resetToDefaultColor()),
      );
    } else if (lock && !gameLockData[lock?.id]?.disable) {
      this.setState({
        gameLockPopOn: true,
        openedLock: lock,
        closedElementPopup: false,
      });
    }
    // change color
    return undefined;
  };

  handleVsObjectLock = (data: any, type: LockLocationType) => {
    const {
      game: { locks },
    } = this.state;

    const lock = locks.find(
      (l: any) =>
        (String(data?.id) === l.locationItem || l.solution?.includes(String(data?.id))) &&
        l.locationType === type,
    );

    if (!lock) {
      // Handle by default
      return true;
    }
    if (gameLockData[lock?.id]?.disable) {
      // nothing happened
      return false;
    }
    if (lock.type === LockType.CollectItems) {
      this.handleCollectLock(lock, String(data.id));
    } else if (lock.type === LockType.Sequence) {
      this.handleSequenceLock(lock, String(data.id), () =>
        this.modelNodes.forEach((node) => node[1] && node[1].resetToDefaultColor()),
      );
    } else if (lock && !gameLockData[lock?.id]?.disable) {
      this.setState({
        gameLockPopOn: true,
        openedLock: lock,
        closedElementPopup: false,
      });
    }
    // change color
    return undefined;
  };

  handleSequenceLock = async (
    lock: GameLock,
    entityId: string,
    resetSelection: () => void = () => undefined,
  ) => {
    const { sequenceUserSolution, locksState, game } = this.state;
    let temp;
    let time;
    if (gameLockData[lock?.id] === undefined)
      gameLockData[lock?.id] = { attempts: 0, disable: false };
    if (lock.multipleAttempts && gameLockData[lock?.id].attempts >= lock.quantityOfAttempts)
      return null;
    this.setMusicEffectForLock(lock);
    if (
      this.resetSequenceUserSolutionAfter === null &&
      !lock.keepHighlight &&
      lock.resetHighlight > 0
    ) {
      this.resetSequenceUserSolutionAfter = _debounce(() => {
        const LockInfoManual: TLockItem = { value: ELockInfo.reset_sequence, obj: lock };
        this.setState({ sequenceUserSolution: '', LockInfoManual });
        resetSelection();
        gameLockData[lock?.id].attempts++;
      }, lock.resetHighlight * 1000);
      this.resetSequenceUserSolutionAfter();
    } else {
      this.resetSequenceUserSolutionAfter?.();
    }
    time = lock.resetHighlight * 1000;
    // eslint-disable-next-line no-unused-expressions
    await audioHandler.playAudio({ type: MusicObj.GAME_SELECTED_AUDIO, id: lock.id });
    if (sequenceUserSolution.length === 0) {
      temp = `${entityId}`;
    } else {
      temp = `${sequenceUserSolution}${LOCK_SOLUTION_SEPARATOR}${entityId}`;
    }
    this.setState({
      sequenceUserSolution: temp,
    });
    if (temp.length === lock.solution.length) {
      this.resetSequenceUserSolutionAfter?.cancel();
      this.showSequenceLockUserResult(lock, temp);
      this.handleMondelezTabletVideos(lock.name);
    } else {
      if (lock?.hint && !locksState[lock?.id]) {
        const hintToCheck = lock?.hint?.hint1_active
          ? 'hint1'
          : lock?.hint?.hint2_active
          ? 'hint2'
          : 'solution';
        if (
          !locksState[lock?.id] ||
          (!locksState[lock?.id][`${hintToCheck}_used`] &&
            !locksState[lock?.id][`${hintToCheck}_time`])
        ) {
          if (lock?.hint?.hint1_unlockTime || game?.nextHintTime) {
            const lockState = locksState[lock?.id] ? locksState[lock?.id] : {};
            this.setState({
              locksState: {
                ...locksState,
                [lock?.id]: {
                  ...lockState,
                  [`${hintToCheck}_time`]:
                    Date.now() + (lock.hint.hint1_unlockTime || game?.nextHintTime) * 1000,
                },
              },
            });
          }
        }
      }
      const LockInfoManual: TLockItem = {
        value: ELockInfo.info_sequence,
        obj: { ...lock, collected: temp?.split(LOCK_SOLUTION_SEPARATOR) },
        time,
      };
      this.setState({ LockInfoManual });
    }
  };

  handleCollectLock = async (lock: GameLock, entityId: string) => {
    const {
      collectItemsBackpack,
      locksState,
      game,
      tagsBackpack,
      polygonsBackpack,
      conditionsBackpack,
      backpackNewItems,
    } = this.state;
    const userSolutionForLock = this.lockUserSolution.get(lock.id);
    if (
      userSolutionForLock &&
      userSolutionForLock.split(LOCK_SOLUTION_SEPARATOR).indexOf(entityId) >= 0
    ) {
      // this item with entityID was find before.
      return;
    }
    const newSolution =
      userSolutionForLock?.length > 0
        ? `${userSolutionForLock}${LOCK_SOLUTION_SEPARATOR}${entityId}`
        : entityId;
    this.lockUserSolution.set(lock.id, newSolution);
    // animation of item moving to backpack
    addToBackpack();
    if (!this.props.isBbOpen) {
      // open backpack when loot something first time @Omer
      if (
        tagsBackpack?.length +
          polygonsBackpack?.length +
          conditionsBackpack?.length +
          collectItemsBackpack?.length ===
        0
      ) {
        this.setState({
          showBackpack: true,
        });
      } else {
        // we don't want to set new items for backpack that will be opened
        this.setState({
          backpackNewItems: backpackNewItems + 1,
        });
      }
    }
    this.setState({
      collectItemsBackpack: [...collectItemsBackpack, lock],
    });
    if (lock?.collected) {
      lock.collected = [...lock?.collected, entityId];
    }
    if (newSolution.length === lock.solution.length) {
      await this.setMusicEffectForLock(lock, 'correctMusic');
      // eslint-disable-next-line no-unused-expressions
      await audioHandler.playAudio({ type: MusicObj.GAME_SELECTED_AUDIO, id: lock.id });
      this.lockSolved(lock, true);
      const LockInfoManual: TLockItem = { value: ELockInfo.ok, obj: lock };
      this.setState({ LockInfoManual });
    } else {
      if (lock?.hint && !locksState[lock?.id]) {
        const hintToCheck = lock?.hint?.hint1_active
          ? 'hint1'
          : lock?.hint?.hint2_active
          ? 'hint2'
          : 'solution';
        if (
          !locksState[lock?.id] ||
          (!locksState[lock?.id][`${hintToCheck}_used`] &&
            !locksState[lock?.id][`${hintToCheck}_time`])
        ) {
          if (lock?.hint?.hint1_unlockTime || game?.nextHintTime) {
            const lockState = locksState[lock?.id] ? locksState[lock?.id] : {};
            this.setState({
              locksState: {
                ...locksState,
                [lock?.id]: {
                  ...lockState,
                  [`${hintToCheck}_time`]:
                    Date.now() + (lock.hint.hint1_unlockTime || game?.nextHintTime) * 1000,
                },
              },
            });
          }
        }
      }
      const LockInfoManual: TLockItem = { value: ELockInfo.info_collect, obj: lock };
      this.setState({ LockInfoManual });
      this.setMusicEffectForLock(lock);
      // eslint-disable-next-line no-unused-expressions
      await audioHandler.playAudio({ type: MusicObj.GAME_SELECTED_AUDIO, id: lock.id });
    }
    if (
      asyncStore.vrIsOn &&
      !DeviceManager.isMobile() &&
      (this.state.tour?.user?.enterprise ||
        this.state.tour?.user?.subscription ===
          (UserSubscription.Business || UserSubscription.Enterprise))
    ) {
      let collectedItem;
      if (lock.locationType === LockLocationType.TransparentBox)
        collectedItem = this.state.polygons.find((item) => +item.id === +entityId);
      if (
        lock.locationType === LockLocationType.vsItem ||
        lock.locationType === LockLocationType.vsVideo ||
        lock.locationType === LockLocationType.vsImage
      )
        collectedItem = this.vsObjects.find((item) => +item.id === +entityId);
      if (lock.locationType === LockLocationType.Tag)
        collectedItem = [...this.state.allTags].find(
          ([key, value]) => value.defaultSid === entityId,
        )[1];
      if (collectedItem) await this.initCollectedPopupVR(lock, collectedItem);
    }
  };

  async initCollectedPopupVR(lock, collectedItem) {
    const html = createCollectHTML(
      `${lock.name} ${lock.collected.length}/${lock.solution.split('|').length}`,
    );
    await this.showCaseTemp.showcase.Scene.register(
      collectPopupComponentType,
      makeCollectPopupComponent,
    );
    const modelNode: any = await this.showCaseTemp.mpSDK.Scene.createNode();
    const component = await modelNode.addComponent(collectPopupComponentType, {
      position: {
        x: collectedItem.positionX,
        y: collectedItem.positionY,
        z: collectedItem.positionZ,
      },
      html,
      pose: this.pose,
    });
    await modelNode.start();
    setTimeout(() => {
      component.destroy();
    }, 3000);
  }

  removeFromCollectItemsBackpack = (ids: number[]) => {
    ids.forEach((id) => {
      this.lockUserSolution.set(id, null);
    });
    this.setState({
      collectItemsBackpack: this.state.collectItemsBackpack.filter(
        (item) => !ids.includes(item.id),
      ),
    });
  };

  addConditionToBackpack = (condition: GameCondition) => {
    const {
      collectItemsBackpack,
      tagsBackpack,
      polygonsBackpack,
      conditionsBackpack,
      backpackNewItems,
    } = this.state;
    if (!this.props.isBbOpen) {
      this.setState({
        backpackNewItems: backpackNewItems + 1,
      });
    }
    addToBackpack();
    if (!this.props.isBbOpen) {
      // open backpack when loot something first time @Omer
      if (
        tagsBackpack?.length +
          polygonsBackpack?.length +
          conditionsBackpack?.length +
          collectItemsBackpack?.length ===
        0
      ) {
        this.setState({
          showBackpack: true,
        });
      } else {
        // we don't want to set new items for backpack that will be opened
        this.setState({
          backpackNewItems: backpackNewItems + 1,
        });
      }
    }
    this.setState({
      conditionsBackpack: [condition, ...conditionsBackpack],
    });
  };

  showConditonPopup = (condition: GameCondition) => {
    this.setState({
      conditionPopUp: condition,
    });
  };

  async getTourBySlug(slug: string) {
    const tourRes = await TreedisPublicDAO.getTourBySlug({ slug });
    if (tourRes && tourRes.message.includes('Success')) {
      this.setState({ tour: new Tour(tourRes.tour), showcaseLoaded: true });
      this.loadData(tourRes.tour);
      this.setWhiteLabel(tourRes.tour);
      this.initTourInfo(tourRes.tour.id);
    } else toast.error('Experience data load error');
    this.setState({ tourLoaded: true });
  }

  closeGameLockPop = async (isCorrect: boolean) => {
    const { openedLock, allTags, game } = this.state;
    // with _cloneDeep game stuck at ~30s
    const newGame = { ...game };
    const completeHex = '#00d084';
    const incompleteHex = '#eb144c';
    if (!openedLock) return;
    if (openedLock.locationType === LockLocationType.Tag) {
      let tags;
      if (openedLock.type === LockType.Sequence) {
        tags = openedLock.solution
          .split(LOCK_SOLUTION_SEPARATOR)
          .map((s) => getTagByLockLocationItem(allTags, s));
      } else {
        tags = [getTagByLockLocationItem(allTags, openedLock.locationItem)];
      }
      console.log('bbb this.state', this.state);
      const activePathId = this.state?.pathfinderDataState?.currentPathStepId;
      if (isCorrect) {
        tags.forEach((tag) => {
          console.log('bbb tag', tag);
          if (!tag) return;
          asyncStore.pushRestoreTagsTimeline(activePathId, tag.sid);
          this.changeTagIconAndColor(tag, 'check', completeHex);
        });
      } else {
        tags.forEach((tag) => {
          console.log('bbb tag', tag);
          if (!tag) return;
          asyncStore.pushRestoreTagsTimeline(activePathId, tag.sid);
          this.changeTagIconAndColor(tag, 'close', incompleteHex);
        });
      }
    } else if (openedLock.locationType === LockLocationType.TransparentBox) {
      // originId
      let items = [];
      if (openedLock.locationItem === EMPTY_LOCATION_ITEM) {
        items = openedLock.solution.split(LOCK_SOLUTION_SEPARATOR).map(Number);
      } else {
        items.push(openedLock.locationItem);
      }

      this.modelNodes.forEach((node) => {
        const component = node[1];
        if (component && items.find((item) => item === component.originId)) {
          if (isCorrect) {
            component.setSelection(completeHex);
          } else {
            component.setSelection(incompleteHex);
          }
        }
      });
    }

    newGame.locks.forEach((lock) => {
      if (lock.id === openedLock.id) {
        if (gameLockData[lock?.id] === undefined)
          gameLockData[lock?.id] = { attempts: 0, disable: false };
        gameLockData[lock?.id].attempts++;
        if (
          isCorrect ||
          (lock.multipleAttempts && gameLockData[lock?.id].attempts >= lock.quantityOfAttempts)
        ) {
          gameLockData[lock?.id].disable = true;
        }
      }
    });

    this.setState({
      game: newGame,
      gameLockPopOn: false,
      openedLock: null,
      userAnswerForGameLock: null,
      closedElementPopup: true,
      isClosedGameGenially: false,
    });
    await audioHandler.resumeMusic();
  };

  lockSolved = (lock: GameLock, result: boolean) => {
    this.setState({ lockSolved: { lock, result } });
  };

  tryButtonPlaySound = async () => {
    const { tour, game } = this.state;
    const { importAudioData, playAudio, changeSelectedAudioVolume } = audioHandler;
    const {
      recoilState: { soundOn, volume },
    } = this.props;
    if (game?.buttonSound && game?.buttonSoundUrl && soundOn) {
      try {
        game.tourId = tour.id;
        await importAudioData(MusicObjectKey.BUTTON, [game]);
        changeSelectedAudioVolume(MusicObjectKey.BUTTON, game.id, volume);
        await playAudio({ type: MusicObj.BUTTON, id: game.id });
      } catch (err: any) {
        console.error('err while trying to play button click sound', err);
      }
    }
  };

  handleGameStart = async (username: string, skipValidation = false) => {
    const { endgameData } = this.state;
    // if (!tour.introScreen) {
    //   this.onIntroScreenFinished();
    // }
    // if (gameStatus !== GameStatus.NotStarted) return;
    if (this.props.endgameData || endgameData) {
      if (!endgameData) {
        this.setEndGameData(this.props.endgameData);
      } else {
        this.setState(endgameData);
      }
      return;
    }
    if (username || skipValidation) {
      this.gameAnalytic?.setName(username);
      this.gameAnalytic?.update();
      this.setState(
        {
          username: username || '',
        },
        async () => {
          await this.tryButtonPlaySound();
        },
      );
    } else toast.error('Enter name please');
  };

  finishAnalytic = () => {
    const { gameStatus } = this.state;
    if (
      [
        GameStatus.SuccessfullyEnded,
        GameStatus.SuccessfullyEndedWithoutFinalScreen,
        GameStatus.TimeIsUpEnded,
        GameStatus.SuccessfullyEndedWithSimpleFinalScreen,
      ].includes(gameStatus)
    ) {
      this.gameAnalytic.update({
        completed: true,
      });
    }
  };

  // render UI
  isRenderAuth = () => {
    return [
      GameStatus.SuccessfullyEnded,
      GameStatus.SuccessfullyEndedWithSimpleFinalScreen,
      GameStatus.OutroClip,
    ].includes(this.state.gameStatus);
  };

  resetMusicAfterEndGame = async () => {
    audioHandler.pauseAllMusic();
  };

  renderAuth = () => {
    if (!this.state.gameLoaded) {
      return <LoadingProgress />;
    }

    switch (this.state.gameStatus) {
      case GameStatus.SuccessfullyEnded:
        this.resetMusicAfterEndGame();
        return (
          <FinishScreen
            isRtl={this.state.tour?.client?.rtl}
            tour={this.state.tour}
            game={this.state.game}
            gameTime={this.state.finalTime}
            gamePoints={this.state.finalPoints}
            username={this.state.username}
            translation={this.state.translation}
            sendRating={(obj) => this.gameAnalytic.update(obj)}
            previousResult={!!this.state.endgameData}
          />
        );
      case GameStatus.SuccessfullyEndedWithSimpleFinalScreen:
        this.resetMusicAfterEndGame();
        return (
          <SimpleFinishScreen
            tour={this.state.tour}
            game={this.state.game}
            username={this.state.username}
            translation={this.state.translation}
            sendRating={(rating) => this.gameAnalytic.update({ rating })}
          />
        );
      case GameStatus.OutroClip:
        this.resetMusicAfterEndGame();
        return (
          <ClipScreen
            subtitles={this.getCurrentLanguage()?.languageCode}
            videoUrl={
              this.state.game.outroClipUrl ||
              `${process.env.NEXT_PUBLIC_CDN}/tours/${this.state.tour.id}/game/${
                this.state.game.id
              }/${this.state.game.uploadedOutroUrl}${getVersion(this.state.game.updatedAt)}`
            }
            close={() => {
              const gameStatus = this.state.game.finishScreenOn
                ? GameStatus.SuccessfullyEnded
                : GameStatus.SuccessfullyEndedWithoutFinalScreen;
              this.setState(
                {
                  gameStatus,
                  lockSolved: null,
                },
                this.finishAnalytic.bind(this),
              );
            }}
          />
        );
      default:
        return null;
    }
  };

  toggleSubtitle = async (stateSubtitle: boolean) => {
    if (stateSubtitle) {
      audioHandler.pauseAllMusic();
    }

    if (!stateSubtitle) {
      this.playTourAudio();
    }
  };

  callExperiencePopup = (obj: any) => {
    this.setState({ ...obj });
  };

  listenSockets = () => {
    if (this.state.host) {
      socket.on('video_user_joined', async (data: any) => {
        const { participantId } = this.state;
        if (!participantId) {
          this.setState({ participantId: data.participantId });
        }
      });
    }
  };

  openVsIframe = async (obj: any, type: string | any) => {
    this.unlockChapter(obj, type);
    if (type === ChapterType.Tag) {
      this.openTagIframe(obj?.sid, { skipEmbedMediaCheck: true });
    } else if (type === ChapterType.VSImage) {
      this.openVsImageIframe(obj);
    } else if (type === ChapterType.VSVideo) {
      this.openVsVideoIframe(obj);
    } else if (type === ChapterType.VSItem) {
      this.openVsObjectIframe(obj);
    } else if (type === ChapterType.VSImage) {
      await this.handleVSAction(obj);
    } else if (type === ChapterType.Polygon && obj?.polygonsActions?.length) {
      this.handlePolygonLock(obj?.id);
      await this.handlePolygonAction(obj.polygonsActions[0]);
    }
  };

  setGameState = async (data: any) => {
    return new Promise((resolve) => {
      this.setState({ game: data }, () => resolve(true));
    });
  };

  renderGameContent = () => {
    const {
      closedElementPopup,
      whiteLabelData,
      game,
      experiencePopUpIsOpen,
      vsActionPopOn,
      gameLockPopOn,
      conditionPopUp,
      sharePopupOn,
      contactPopupOn,
      tagPopOn,
      currentCondition,
      // experiencePopUpData,
    } = this.state;

    const isOpenedContentPopUp = experiencePopUpIsOpen || vsActionPopOn;

    const popupsOpen =
      !!gameLockPopOn ||
      !!conditionPopUp ||
      !!sharePopupOn ||
      !!contactPopupOn ||
      !!vsActionPopOn ||
      !!tagPopOn ||
      !!currentCondition ||
      !!experiencePopUpIsOpen ||
      !!vsActionPopOn;

    // Store popupsOpen in state for use in componentDidUpdate
    // Using requestAnimationFrame to avoid calling setState during render
    if (this.state.popupsOpen !== popupsOpen) {
      requestAnimationFrame(() => {
        this.setState({ popupsOpen });
      });
    }
    return this.isTourReadyForAudio() && !!game && !this.state.loadingTags ? (
      <GameplayScreen
        isSubtitleVisible={this.state.isSubtitleVisible}
        toggleVisibleTags={this.toggleVisibleTags}
        pose={this.pose}
        THREE={this.THREE}
        changeGameCondition={this.changeGameCondition}
        renderer={this.renderer}
        camera={this.camera}
        setRotation={this.showCaseTemp?.mpSDK?.Camera.setRotation}
        clickedElement={this.state.clickedElement}
        panoIsOpen={this.state.panoramaOn}
        pathfinderDataState={this.state.pathfinderDataState}
        setPathfinderDataState={this.setPathfinderDataState}
        backpackNewItems={this.state.backpackNewItems}
        conditionToBackpack={this.addConditionToBackpack}
        removeFromCollectItemsBackpack={this.removeFromCollectItemsBackpack}
        currentSweep={this.state.currentSweep}
        filterVsElementsByGameCondition={this.filterVsElementsByGameCondition}
        isOpenedContentPopUp={isOpenedContentPopUp}
        availableSweeps={this.state?.sweeps?.map((s) => s.sid)}
        translation={this.state.translation}
        initialDuplicates={this.state.initialDuplicates}
        ticketTimeLeftMs={this.state.ticketTimeLeftMs}
        ticketing={this.state.ticketing}
        game={game}
        tour={this.state.tour}
        tourType={this.state.tourType}
        showcaseLoaded={this.state.showcaseLoaded}
        activeHintObject={this.state.activeHintObject}
        username={this.state.username}
        allTags={this.state.allTags}
        gameStatus={this.state.gameStatus}
        initPolygon={this.initPolygonNode}
        lockSolved={this.state.lockSolved}
        modelNodes={this.modelNodes}
        polygons={this.state.polygons}
        showcase={this.showcase}
        showHintModal={this.state.showHintModal}
        removeFromClickedElements={this.removeFromClickedElements}
        setActiveHintObject={(activeHintObject: any) => this.setState({ activeHintObject })}
        onGameEnd={async () => {
          this.setState({
            finalTime: this.gameAnalytic.getTime(),
            finalPoints: this.gameAnalytic.getPoint(),
          });
        }}
        analytic={this.gameAnalytic}
        setShowHintModal={(showHintModal: boolean) => this.setState({ showHintModal })}
        setGameStatus={(gameStatus: GameStatus) => {
          this.setState({ gameStatus }, () => {
            this.finishAnalytic.bind(this);
          });
        }}
        vsObjects={this.vsObjects}
        initImageNode={this.initImageNode}
        initVideoNode={this.initVideoNode}
        initNode={this.initNode}
        showBookBtn={game?.gameBookOn && game?.book && !this.state.showBook}
        closeGameBook={() => this.setState({ showBook: false })}
        unlockedChapterIds={this.state.unlockedChapterIds}
        sweeps={this.state.sweeps}
        clickedElements={this.state.clickedElements}
        tryButtonPlaySound={this.tryButtonPlaySound}
        closedElementPopup={closedElementPopup}
        onToggleGameBookModal={() => this.setState((state) => ({ showBook: !state.showBook }))}
        showGuide={this.state.guide}
        toggleGuide={() => this.setState({ guide: false })}
        whiteLabel={whiteLabelData}
        callExperiencePopup={this.callExperiencePopup}
        moveToSweep={this.moveToSweep}
        setVSVisible={this.setVSVisible}
        openedLock={this.state.openedLock}
        handleCurrentCondition={(condition) => this.setState({ currentCondition: condition })}
        setGameState={this.setGameState}
        openVsIframe={this.openVsIframe}
        isClosedGameGenially={this.state.isClosedGameGenially}
        // Sidebar
        showBackpack={this.state.showBackpack}
        showBook={this.state.showBook}
        setBaseState={this.setBaseState}
        toggleVS={this.toggleVS}
        isVSEnabled={this.state.isVSEnabled}
        isShowPathLockByExperience={this.state.isShowPathLockByExperience}
        // hub
        showParticipantsIcon={Boolean(this.state.reelVideos?.length)}
        showParticipantsReel={this.state.showParticipantsReel}
        host={this.state.host}
        participantId={this.state.participantId}
        // audio
        mainPlaying={this.state.mainPlaying}
        isEnableNextStep={this.state.isEnableNextStep}
        setPassedPath={this.setPassedPath}
        getTagIconId={this.getTagIconId}
        registerIcon={this.registerIcon}
        editIcon={this.showcase.Tag.editIcon}
        resetIcon={this.showcase.Tag.resetIcon}
        distanceToNextPathStep={this.state.distanceToNextPathStep}
        vrEventManager={this.vrEventManager}
      />
    ) : (
      <LoadingProgress />
    );
  };

  colorBackSeqPolygons = (seqSol: string) => () => {
    const polyIds = seqSol.split('|').map((e) => Number(e));
    this.modelNodes.forEach(([, polyNode]) => {
      if (polyIds.includes(polyNode.originId)) {
        polyNode.resetToDefaultColor();
      }
    });
  };

  getGeniallyUrlForVSHTML = (id: string) => {
    let url = '';
    this.vsObjects.forEach((item) => {
      if (item instanceof TourVSHtml && item.id === parseInt(id, 10)) {
        // item.node.onEvent(ComponentInteractionType.CLICK);
        url = item?.details?.url;
      }
    });
    return url;
  };

  removeFromClickedElements = (itemsToRemove: { id: number | string; type: string }[]) => {
    console.log('bbb removeFromClickedElements', itemsToRemove);
    if (!itemsToRemove) return;
    this.setState({
      clickedElements: this.state.clickedElements?.filter((clickedElement) => {
        return itemsToRemove?.find(
          (itemToRemove) =>
            itemToRemove?.type === clickedElement.type &&
            (itemToRemove?.id === clickedElement.value ||
              itemToRemove?.id === clickedElement.sid ||
              itemToRemove?.id === clickedElement.defaultSid ||
              itemToRemove?.id === clickedElement.value?.toString()),
        );
      }),
    });
  };

  injectChildComponents = () => {
    const {
      tour,
      game,
      gameLockPopOn,
      showNewHintModal,
      userAnswerForGameLock,
      openedLock,
      conditionPopUp,
      translation,
      initialDuplicates,
      showConfetti,
    } = this.state;

    const setShowConfetti = (v: boolean) => this.setState({ showConfetti: v });
    const setShowNewHintModal = (show: null | number) => this.setState({ showNewHintModal: show });
    const setLocksState = (locksState) => this.setState({ locksState });

    return (
      <>
        {/* Specific temporary functionality for a Mondelez tour */}
        {this.state.mondelezTabletVideoId && (
          <MondelezTabletVideo
            videoId={this.state.mondelezTabletVideoId}
            onClose={() => this.setState({ mondelezTabletVideoId: undefined })}
          />
        )}

        <ScreenNavigationArrow refProp={this.pathArrowRef} />
        <ConfettiVisualEffect showConfetti={showConfetti} setShowConfetti={setShowConfetti} />

        <ConditionModal
          show={!!conditionPopUp}
          gameId={tour.gameId}
          condition={conditionPopUp}
          onClose={this.closeConditionPopUp}
          initialDuplicates={initialDuplicates}
        />
        {(showNewHintModal && (
          <NewHintModal
            rtl={tour.client?.rtl}
            translation={translation}
            setLocksState={setLocksState}
            locksState={this.state.locksState}
            analytic={this.gameAnalytic}
            initialDuplicates={initialDuplicates}
            game={game}
            setShowNewHintModal={setShowNewHintModal}
            activeLockId={showNewHintModal}
            hints={this.state?.game?.hints}
          />
        )) ||
          (gameLockPopOn && (
            <GameLockPopup
              colorBackSeqPolygons={this.colorBackSeqPolygons}
              nextHintTime={game?.nextHintTime}
              setLocksState={setLocksState}
              locksState={this.state.locksState}
              setShowNewHintModal={setShowNewHintModal}
              setGameLockPopOn={(v: boolean) => this.setState({ gameLockPopOn: v })}
              setShowConfetti={setShowConfetti}
              gameLockPopOn={gameLockPopOn}
              tryButtonPlaySound={this.tryButtonPlaySound}
              lock={openedLock}
              gameId={game.id}
              isRtl={tour.client?.rtl}
              translation={translation}
              closePop={this.closeGameLockPop}
              userAnswer={userAnswerForGameLock}
              onHandleAnswer={this.lockSolved}
              setClosedGameGenially={(value: boolean) =>
                this.setState({ isClosedGameGenially: value })
              }
              getGeniallyUrlForVSHTML={this.getGeniallyUrlForVSHTML}
            />
          ))}

        <LockInfoPopup
          answerIndicatorOn={this.state?.game?.answerIndicatorOn}
          rtl={tour.client?.rtl}
          translation={translation}
          setShowNewHintModal={setShowNewHintModal}
          manualData={this.state.LockInfoManual}
          setManualData={(data) => this.setState({ LockInfoManual: data })}
        />
      </>
    );
  };

  // prevent unnecessary renders functions
  toggleBbIsOpen = () => {
    this.props.setIsBbOpen((v: boolean) => !v);
    this.setState((prevState) => ({
      showBackpack: !prevState.showBackpack,
    }));
  };

  toggleHintModal = () => {
    const { resumeMusic, stopTourMusic } = audioHandler;
    this.setState(
      (state) => ({ showHintModal: !state.showHintModal }),
      async () => {
        if (!this.state.showHintModal) {
          await resumeMusic();
        } else {
          await stopTourMusic();
        }
      },
    );
  };

  clickOnTagBackpack = (tag) => this.openTagIframe(tag.sid);

  setBackpackNewItems = (backpackNewItems: number) => this.setState({ backpackNewItems });

  onHelpClick = () => this.setState({ showHelpModal: true, showTermsModal: false });

  public renderBottomBar(isFullScreen, toggleFullscreen) {
    return this.state.game && !this.state.showIntro && !this.state.startScreenOn ? (
      <BottomBar
        showBackpackByDefault={this.state.game.showBackpackByDefault}
        isFull={isFullScreen}
        changeFull={toggleFullscreen}
        onToggleBackpackModal={this.toggleBbIsOpen}
        onToggleHintModal={this.toggleHintModal}
        hintDisable={
          !this.state.activeHintObject ||
          (!this.state.game.hint1 && !this.state.game.hint2 && !this.state.game.solution)
        }
        tourId={this.state.tour?.id}
        tags={this.state.tagsBackpack}
        polygons={this.state.polygonsBackpack}
        conditions={this.state.conditionsBackpack}
        collectItems={this.state.collectItemsBackpack}
        clickOnTagBackpack={this.clickOnTagBackpack}
        clickOnPolygonBackpack={this.handlePolygonAction}
        clickOnConditionBackpack={this.showConditonPopup}
        isRtl={this.state.tour?.client?.rtl}
        translation={this.state.translation}
        tour={this.state.tour}
        handleToggleMusic={this.handleToggleMusic}
        mainPlaying={this.state.mainPlaying}
        hideBackpack={!this.state.game.backpackOn}
        showBackpack={this.state.showBackpack}
        setBackpackNewItems={this.setBackpackNewItems}
        gameHasStarted={this.state.gameStatus === GameStatus.Started}
        // hub props
        showParticipantsReel={this.state.showParticipantsReel}
        host={this.state.host}
        reelVideos={this.state.reelVideos}
        audioOnly={this.state.audioOnly}
        onMuteClick={this.toggleMuteParticipants}
        onFullscreenClick={this.toggleFullscreenVideo}
        onPinClick={this.addPinnedVideo}
        whiteLabelData={this.state.whiteLabelData}
        onHelpClick={this.onHelpClick}
      />
    ) : null;
  }
}
